import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, CircularProgress, LinearProgress, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PipelineVerboseStep } from "@/features/chat/lib/turnStream";

type ChatProcessingPanelProps = {
  currentVerboseStep: PipelineVerboseStep | null;
  verboseLog: PipelineVerboseStep[];
};

export function ChatProcessingPanel({
  currentVerboseStep,
  verboseLog,
}: ChatProcessingPanelProps) {
  const { t } = useTranslation("chat");
  const [expanded, setExpanded] = useState(false);
  const logEndRef = useRef<HTMLDivElement>(null);
  const verboseHeading = currentVerboseStep?.summary ?? t("processingWaiting");

  useEffect(() => {
    if (
      expanded &&
      logEndRef.current &&
      typeof logEndRef.current.scrollIntoView === "function"
    ) {
      logEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [expanded, verboseLog.length]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 640,
          borderRadius: 2,
          border: "1px solid var(--theme-border)",
          bgcolor: "var(--theme-surface)",
          px: 2,
          py: 1.75,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: "var(--theme-text-primary)", mb: 1.25 }}
        >
          {t("processingTitle")}
        </Typography>
        <LinearProgress
          variant="indeterminate"
          sx={{
            mb: 1.5,
            height: 3,
            borderRadius: 999,
            bgcolor: "var(--theme-overlay-light-10)",
            "& .MuiLinearProgress-bar": {
              bgcolor: "var(--theme-text-secondary)",
            },
          }}
        />
        <Box
          sx={{
            borderRadius: 1.5,
            bgcolor: "var(--theme-background)",
            border: "1px solid var(--theme-border)",
            overflow: "hidden",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded((value) => !value)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              width: "100%",
              border: "none",
              bgcolor: "transparent",
              px: 1.5,
              py: 1,
              minHeight: 40,
              cursor: "pointer",
              textAlign: "left",
            }}
            aria-expanded={expanded}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "var(--theme-primary-light, var(--theme-primary))",
                  fontWeight: 600,
                }}
              >
                {verboseHeading}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <CircularProgress size={16} sx={{ color: "var(--theme-primary)" }} />
              {expanded ? (
                <KeyboardArrowUpIcon
                  sx={{ fontSize: 18, color: "var(--theme-text-secondary)" }}
                  aria-hidden
                />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 18, color: "var(--theme-text-secondary)" }}
                  aria-hidden
                />
              )}
            </Box>
          </Box>
          {expanded ? (
            <Box
              sx={{
                px: 1.5,
                pb: 1.5,
                maxHeight: 240,
                overflowY: "auto",
                borderTop: "1px solid var(--theme-border)",
              }}
            >
              {verboseLog.length === 0 ? (
                <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)", pt: 1 }}>
                  {t("processingWaiting")}
                </Typography>
              ) : (
                verboseLog.map((step) => (
                  <Box key={`${step.stepIndex}-${step.eventType}`} sx={{ pt: 1.25 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "var(--theme-text-primary)" }}
                    >
                      {step.stepTitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "var(--theme-text-secondary)",
                        whiteSpace: "pre-wrap",
                        mt: 0.5,
                      }}
                    >
                      {step.detail}
                    </Typography>
                  </Box>
                ))
              )}
              <Box ref={logEndRef} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
